if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.handleThumbnailMaskState = this.updateThumbnailMaskState.bind(this);
        this.elements.thumbnails.addEventListener('scroll', this.handleThumbnailMaskState);
        window.addEventListener('resize', this.handleThumbnailMaskState);
        this.updateThumbnailMaskState();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const activeItem = event.detail.currentElement;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
          item.classList.toggle('is-active', item === activeItem);
        });

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeItem.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(activeItem);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const isVerticalThumbnails = this.dataset.thumbnailType === 'vertical' && this.mql.matches;
        if (isVerticalThumbnails) {
          this.elements.thumbnails.scrollTo({ top: thumbnail.offsetTop - thumbnail.offsetHeight - 16, behavior: 'smooth' });
          this.updateThumbnailMaskState();
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        const slider = this.elements.thumbnails.slider;
        const offset = Math.max(0, thumbnail.offsetLeft - (slider.clientWidth - thumbnail.clientWidth) / 2);
        slider.scrollTo({ left: offset, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        let video = activeItem.querySelector('video');

        if (video && !video.paused) return;

        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('.deferred-media');

        if (!deferredMedia) return;

        deferredMedia.loadContent(false);

        video = activeItem.querySelector('video');

        if (!video) return;

        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;

        const clearPoster = () => video.removeAttribute('poster');
        video.addEventListener('playing', clearPoster, { once: true });

        video.play().catch((error) => {
          console.warn('Product video autoplay failed:', error.name, error.message);
        });
      }

      disconnectedCallback() {
        if (this.elements?.thumbnails && this.handleThumbnailMaskState) {
          this.elements.thumbnails.removeEventListener('scroll', this.handleThumbnailMaskState);
        }
        if (this.handleThumbnailMaskState) {
          window.removeEventListener('resize', this.handleThumbnailMaskState);
        }
      }

      updateThumbnailMaskState() {
        if (!this.elements?.thumbnails) return;
        const isVerticalThumbnails = this.dataset.thumbnailType === 'vertical' && this.mql.matches;
        if (!isVerticalThumbnails) {
          this.elements.thumbnails.classList.remove('thumbnail-slider--at-end');
          return;
        }

        const scrollContainer = this.elements.thumbnails;
        const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
        const isAtEnd = maxScrollTop <= 0 || scrollContainer.scrollTop >= maxScrollTop - 2;
        scrollContainer.classList.toggle('thumbnail-slider--at-end', isAtEnd);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
